import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  api,
  Stats,
  Job,
  Agent,
  Paginated,
  JobTemplate,
  Message,
  User,
  JobDraft,
  ForumThread,
  ForumReply,
  ForumThreadDetail,
} from "@/lib/api";

// --- Stats ---
export const useStats = () => {
  return useQuery<Stats>({
    queryKey: ["stats"],
    queryFn: async () => {
      const { data } = await api.get("/stats");
      return data.data;
    },
    staleTime: 60 * 1000, // 1 min
  });
};

// --- Users ---
export const useMe = () => {
  return useQuery<User>({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await api.get("/users/me");
      return data.data;
    },
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.patch("/users/me", payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};

// --- Jobs ---
export const useJobs = (filters?: Record<string, any>) => {
  return useInfiniteQuery<Paginated<Job>>({
    queryKey: ["jobs", filters],
    queryFn: async ({ pageParam = undefined }) => {
      const { data } = await api.get("/jobs", {
        params: { ...filters, cursor: pageParam, limit: 10 },
      });
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.meta.nextCursor,
    initialPageParam: undefined,
  });
};

export const useMyJobs = (filters?: Record<string, any>) => {
  return useQuery<Paginated<Job>>({
    queryKey: ["my-jobs", filters],
    queryFn: async () => {
      const { data } = await api.get("/users/me/jobs", { params: filters });
      return data;
    },
  });
};

export const useJobDetail = (id: string) => {
  return useQuery<Job>({
    queryKey: ["job", id],
    queryFn: async () => {
      const { data } = await api.get(`/jobs/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
};

export interface CreateJobPayload {
  title: string;
  templateId: string;
  inputData: Record<string, any>;
  budgetUsdc: number;
  deadlineAt?: string;
  requiredPackId?: string;
  acceptanceCriteria?: any;
  paymentProvider?: string;
  [key: string]: any;
}

export const useCreateJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateJobPayload) => {
      const { data } = await api.post("/jobs", payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
};

// --- Job Drafts (DB-persisted wizard state) ---
// Responses go through the global { data } envelope interceptor, so unwrap.
export const useMyDrafts = () =>
  useQuery<JobDraft[]>({
    queryKey: ["my-drafts"],
    queryFn: async () => {
      const { data } = await api.get("/jobs/drafts");
      return data.data ?? data;
    },
  });

export const useSaveDraft = () => {
  const queryClient = useQueryClient();
  return useMutation<JobDraft, any, { draftId: string | null; data: any }>({
    mutationFn: async ({ draftId, data }) => {
      const res = draftId
        ? await api.patch(`/jobs/drafts/${draftId}`, data)
        : await api.post(`/jobs/drafts`, data);
      return res.data.data ?? res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-drafts"] }),
  });
};

export const useDeleteDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.delete(`/jobs/drafts/${id}`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-drafts"] }),
  });
};

export const useUpdateJob = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const { data: response } = await api.patch(`/jobs/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["job", id] });
      queryClient.invalidateQueries({ queryKey: ["my-jobs"] });
    },
  });
};

export const useJobTransition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
      payload,
    }: {
      id: string;
      action: string;
      payload?: any;
    }) => {
      const { data } = await api.patch(`/jobs/${id}/${action}`, payload);
      return data.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["job", id] });
      queryClient.invalidateQueries({ queryKey: ["job-events", id] });
      queryClient.invalidateQueries({ queryKey: ["my-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
};

export const useJobEvents = (id: string) => {
  return useQuery<any[]>({
    queryKey: ["job-events", id],
    queryFn: async () => {
      const { data } = await api.get(`/jobs/${id}/events`);
      return data.data;
    },
    enabled: !!id,
  });
};

export const useJobReports = (id: string) => {
  return useQuery<any[]>({
    queryKey: ["job-reports", id],
    queryFn: async () => {
      const { data } = await api.get(`/jobs/${id}/reports`);
      return (data as any).data || data;
    },
    enabled: !!id,
    refetchInterval: 30000, // Refresh reports every 30s
  });
};

// --- Bidding ---
export const usePlaceBid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      jobId,
      agentId,
      proposedUsdc,
      coverLetter,
    }: {
      jobId: string;
      agentId: string;
      proposedUsdc: string;
      coverLetter?: string;
    }) => {
      const { data } = await api.post(`/jobs/${jobId}/bids`, {
        agentId,
        proposedUsdc,
        coverLetter,
      });
      return data.data;
    },
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ["job-bids", jobId] });
      queryClient.invalidateQueries({ queryKey: ["bid-allowance"] });
    },
  });
};

export const useJobBids = (jobId: string) => {
  return useQuery<any[]>({
    queryKey: ["job-bids", jobId],
    queryFn: async () => {
      const { data } = await api.get(`/jobs/${jobId}/bids`);
      return data.data;
    },
    enabled: !!jobId,
  });
};

export const useAcceptBid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, bidId }: { jobId: string; bidId: string }) => {
      const { data } = await api.post(`/jobs/${jobId}/bids/${bidId}/accept`);
      return data.data;
    },
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ["job", jobId] });
      queryClient.invalidateQueries({ queryKey: ["job-bids", jobId] });
    },
  });
};

export const useBidAllowance = (agentId?: string) => {
  return useQuery<any>({
    queryKey: ["bid-allowance", agentId],
    queryFn: async () => {
      const { data } = await api.get(`/bids/allowance/${agentId}`);
      return data.data;
    },
    enabled: !!agentId,
  });
};

export const useBuyExtraBids = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      agentId,
      quantity,
      usdcAmount,
    }: {
      agentId: string;
      quantity?: number;
      usdcAmount?: number;
    }) => {
      const { data } = await api.post(`/bids/allowance/${agentId}/buy`, {
        quantity,
        usdcAmount,
      });
      return data.data;
    },
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: ["bid-allowance", agentId] });
    },
  });
};

// --- Activity ---
export const useActivity = (filters?: Record<string, any>) => {
  return useInfiniteQuery<Paginated<any>>({
    queryKey: ["activity", filters],
    queryFn: async ({ pageParam = undefined }) => {
      const { data } = await api.get("/activity", {
        params: { ...filters, cursor: pageParam, limit: 10 },
      });
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.meta.nextCursor,
    initialPageParam: undefined,
  });
};

// --- Agents ---
export const useMyAgents = () => {
  return useQuery<Agent[]>({
    queryKey: ["my-agents"],
    queryFn: async () => {
      const { data } = await api.get("/users/me/agents");
      return data.data;
    },
  });
};

export const useAgentDetail = (id: string) => {
  return useQuery<Agent>({
    queryKey: ["agent", id],
    queryFn: async () => {
      const { data } = await api.get(`/agents/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
};

export const useRegisterAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post("/agents", payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-agents"] });
    },
  });
};

export const useUpdateAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const { data: response } = await api.patch(`/agents/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["agent", id] });
      queryClient.invalidateQueries({ queryKey: ["my-agents"] });
    },
  });
};

export const useVerifyAgentMail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, token }: { id: string; token: string }) => {
      const { data } = await api.post(`/agents/${id}/agentmail/verify`, {
        token,
      });
      return data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["agent", id] });
      queryClient.invalidateQueries({ queryKey: ["my-agents"] });
    },
  });
};

export const useResendAgentMailVerification = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/agents/${id}/agentmail/resend`);
      return data;
    },
  });
};

// --- Disputes ---
export const useDisputes = (query?: Record<string, any>) => {
  return useQuery<Paginated<any>>({
    queryKey: ["disputes", query],
    queryFn: async () => {
      const { data } = await api.get("/disputes", { params: query });
      return data;
    },
  });
};

export const useDisputeDetail = (id: string) => {
  return useQuery<any>({
    queryKey: ["dispute", id],
    queryFn: async () => {
      const { data } = await api.get(`/disputes/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
};

export const useResolveDispute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: string;
      status: string;
      resolutionNote?: string;
      infoRequestedFrom?: string;
      infoRequestDetails?: string;
      posterBps?: number;
    }) => {
      const { id, ...body } = payload;
      const { data } = await api.patch(`/disputes/${id}/resolve`, body);
      return data.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["disputes"] });
      queryClient.invalidateQueries({ queryKey: ["dispute", id] });
    },
  });
};

export const useCloseDispute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.patch(`/disputes/${id}/close`);
      return data.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["disputes"] });
      queryClient.invalidateQueries({ queryKey: ["dispute", id] });
    },
  });
};

// --- Messages ---
export const useMessages = () => {
  return useQuery<Message[]>({
    queryKey: ["messages"],
    queryFn: async () => {
      const { data } = await api.get("/messages");
      // Unwrap if wrapped
      return (data as any).data || data;
    },
  });
};

export const useConversation = (userId: string) => {
  return useQuery<Message[]>({
    queryKey: ["conversation", userId],
    queryFn: async () => {
      const { data } = await api.get(`/messages/conversation/${userId}`);
      return (data as any).data || data;
    },
    enabled: !!userId,
  });
};

export const useSendMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      receiverId?: string;
      agentId?: string;
      content: string;
      subject?: string;
    }) => {
      const { data } = await api.post("/messages", payload);
      return (data as any).data || data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      if (variables.receiverId) {
        queryClient.invalidateQueries({
          queryKey: ["conversation", variables.receiverId],
        });
      }
    },
  });
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: string) => {
      await api.patch(`/messages/${messageId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },
  });
};

export const useUnreadMessageCount = () => {
  return useQuery<{ count: number }>({
    queryKey: ["unread-messages"],
    queryFn: async () => {
      const { data } = await api.get("/messages/unread-count");
      return (data as any).data || data;
    },
    refetchInterval: 30000, // Poll every 30s
  });
};

// --- Templates ---
export const useTemplates = (filters?: Record<string, any>) => {
  return useQuery<Paginated<JobTemplate>>({
    queryKey: ["templates", filters],
    queryFn: async () => {
      const { data } = await api.get("/templates", { params: filters });
      return data;
    },
  });
};

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post("/templates", payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const { data: response } = await api.patch(`/templates/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });
};

// --- Admin ---
export const usePaymentsOverview = () =>
  useQuery({
    queryKey: ["admin-payments-overview"],
    queryFn: async () => {
      const { data } = await api.get("/admin/payments/overview");
      return data.data ?? data;
    },
    refetchInterval: 30000,
  });

export const useRecentPayments = (limit = 100) =>
  useQuery({
    queryKey: ["admin-payments-recent", limit],
    queryFn: async () => {
      const { data } = await api.get(`/admin/payments/recent?limit=${limit}`);
      return data.data ?? data;
    },
  });

export const useTreasury = () => {
  return useQuery({
    queryKey: ["admin-treasury"],
    queryFn: async () => {
      const { data } = await api.get("/admin/treasury");
      return data.data;
    },
    refetchInterval: 30_000, // poll every 30s
  });
};

export const useBlockchainInfo = () => {
  return useQuery({
    queryKey: ["admin-blockchain-info"],
    queryFn: async () => {
      const { data } = await api.get("/admin/blockchain/info");
      return data.data;
    },
    refetchInterval: 30_000,
  });
};

export const useSweepDeployer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/admin/blockchain/sweep-deployer");
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blockchain-info"] });
    },
  });
};

export const useWithdrawFees = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/admin/blockchain/withdraw-fees");
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blockchain-info"] });
      queryClient.invalidateQueries({ queryKey: ["admin-treasury"] });
    },
  });
};

export const useTransferOwnership = () => {
  return useMutation({
    mutationFn: async (newOwner: string) => {
      const { data } = await api.post("/admin/blockchain/transfer-ownership", {
        newOwner,
      });
      return data.data;
    },
  });
};

export const useTreasuryPayout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      to: string;
      amount: string;
      token: "ETH" | "USDC";
    }) => {
      const { data } = await api.post(
        "/admin/blockchain/treasury-payout",
        payload,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-treasury"] });
      queryClient.invalidateQueries({ queryKey: ["admin-blockchain-info"] });
    },
  });
};

// ── Escrow Operations ──────────────────────────────────────────────────────

export type EscrowRow = {
  jobId: string;
  title: string;
  status: string;
  escrowTxHash: string | null;
  budgetUsdc: number;
  createdAt: string;
  onChain: {
    poster: string;
    agent: string;
    amountUsdc: string;
    platformFeeUsdc: string;
    depositTime: number;
    agentAssigned: boolean;
    released: boolean;
    refunded: boolean;
    disputed: boolean;
    disputeResolved: boolean;
  } | null;
};

export const useAdminEscrows = () => {
  return useQuery({
    queryKey: ["admin-blockchain-escrows"],
    queryFn: async () => {
      const { data } = await api.get("/admin/blockchain/escrows");
      return data.data as EscrowRow[];
    },
    refetchInterval: 30_000,
  });
};

export const useReleaseEscrow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data } = await api.post(
        `/admin/blockchain/escrows/${jobId}/release`,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blockchain-escrows"] });
    },
  });
};

export const useRefundEscrow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data } = await api.post(
        `/admin/blockchain/escrows/${jobId}/refund`,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blockchain-escrows"] });
    },
  });
};

export const useResolveEscrow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      jobId,
      posterBps,
      agentBps,
    }: {
      jobId: string;
      posterBps: number;
      agentBps: number;
    }) => {
      const { data } = await api.post(
        `/admin/blockchain/escrows/${jobId}/resolve`,
        { posterBps, agentBps },
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blockchain-escrows"] });
    },
  });
};

// --- Quiz & Certifications ---
export const useQuizzes = () => {
  return useQuery({
    queryKey: ["quizzes"],
    queryFn: async () => {
      const { data } = await api.get("/evals");
      return data.data ?? data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useQuiz = (id: string) => {
  return useQuery({
    queryKey: ["quiz", id],
    queryFn: async () => {
      const { data } = await api.get(`/evals/${id}`);
      return data.data ?? data;
    },
    enabled: !!id,
  });
};

export const useAgentCertifications = (agentId: string) => {
  return useQuery({
    queryKey: ["certifications", agentId],
    queryFn: async () => {
      const { data } = await api.get(`/evals/agents/${agentId}/certifications`);
      return data.data ?? data;
    },
    enabled: !!agentId,
  });
};

export const useStartQuiz = () => {
  return useMutation({
    mutationFn: async ({
      quizId,
      paidTxHash,
      agentId,
    }: {
      quizId: string;
      paidTxHash?: string;
      agentId?: string;
    }) => {
      const { data } = await api.post(`/evals/${quizId}/start`, {
        paidTxHash,
        agentId,
      });
      return data.data ?? data;
    },
  });
};

export const useAnswerQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      attemptId,
      questionId,
      answer,
    }: {
      attemptId: string;
      questionId: string;
      answer: string;
    }) => {
      const { data } = await api.post(`/evals/attempts/${attemptId}/answer`, {
        questionId,
        answer,
      });
      return data.data ?? data;
    },
    onSuccess: (result) => {
      if (result?.completed) {
        queryClient.invalidateQueries({ queryKey: ["quizzes"] });
        queryClient.invalidateQueries({ queryKey: ["certifications"] });
        queryClient.invalidateQueries({ queryKey: ["my-agents"] });
      }
    },
  });
};

export const usePayQuizFromAgentWallet = () => {
  return useMutation({
    mutationFn: async ({
      quizId,
      agentId,
    }: {
      quizId: string;
      agentId?: string;
    }) => {
      const { data } = await api.post(`/evals/${quizId}/pay-agent-wallet`, {
        agentId,
      });
      return data.data ?? data;
    },
  });
};

// --- Admin Quiz Management ---
export const useAdminQuizzes = () => {
  return useQuery({
    queryKey: ["admin-quizzes"],
    queryFn: async () => {
      const { data } = await api.get("/evals/admin/all");
      return data.data ?? data;
    },
  });
};

export const useAdminQuiz = (id: string) => {
  return useQuery({
    queryKey: ["admin-quiz", id],
    queryFn: async () => {
      const { data } = await api.get(`/evals/admin/${id}`);
      return data.data ?? data;
    },
    enabled: !!id,
  });
};

export const useCreateQuiz = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post("/evals/admin", payload);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-quizzes"] });
      queryClient.invalidateQueries({ queryKey: ["quizzes"] });
    },
  });
};

export const useUpdateQuiz = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: any) => {
      const { data } = await api.patch(`/evals/admin/${id}`, payload);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-quizzes"] });
      queryClient.invalidateQueries({ queryKey: ["quizzes"] });
    },
  });
};

export const useReplaceQuizQuestions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, questions }: { id: string; questions: any[] }) => {
      const { data } = await api.put(`/evals/admin/${id}/questions`, {
        questions,
      });
      return data.data ?? data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-quiz", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-quizzes"] });
    },
  });
};

export const useDeleteQuiz = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/evals/admin/${id}`);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-quizzes"] });
      queryClient.invalidateQueries({ queryKey: ["quizzes"] });
    },
  });
};

export const useImportQuiz = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post("/evals/admin/import", payload);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-quizzes"] });
      queryClient.invalidateQueries({ queryKey: ["quizzes"] });
    },
  });
};

// --- Quizzes ---

export const useQuizSessionStatus = (evalId: string, enabled = true) => {
  return useQuery({
    queryKey: ["quiz-session-status", evalId],
    queryFn: async () => {
      const { data } = await api.get(`/evals/${evalId}/status`);
      return data.data ?? data;
    },
    enabled: !!evalId && enabled,
    refetchInterval: (query) => {
      const s = (query.state.data as any)?.status;
      // Stop polling once terminal state reached
      if (s === "COMPLETED" || s === "TIMED_OUT" || s === "ABANDONED")
        return false;
      return 5000;
    },
    retry: (failureCount, error: any) => {
      const status = error?.response?.status;
      if (status === 404 || status === 403) return false;
      return failureCount < 2;
    },
  });
};

export const useQuizPacks = () => {
  return useQuery({
    queryKey: ["quiz-packs"],
    queryFn: async () => {
      const { data } = await api.get("/evals/packs");
      return data.data ?? data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

// --- Community eval pack authoring + management ---

export const useMyEvalPacks = () => {
  return useQuery({
    queryKey: ["my-eval-packs"],
    queryFn: async () => {
      const { data } = await api.get("/evals/packs/mine");
      return data.data ?? data;
    },
  });
};

export const useMyEvalPack = (packId?: string) => {
  return useQuery({
    queryKey: ["my-eval-pack", packId],
    queryFn: async () => {
      const { data } = await api.get(`/evals/packs/mine/${packId}`);
      return data.data ?? data;
    },
    enabled: !!packId,
  });
};

export const usePublishEvalPack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pack: Record<string, unknown>) => {
      const { data } = await api.post("/evals/packs", pack);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-eval-packs"] });
      queryClient.invalidateQueries({ queryKey: ["quiz-packs"] });
    },
  });
};

export const useSetMyEvalPackActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      packId,
      isActive,
    }: {
      packId: string;
      isActive: boolean;
    }) => {
      const { data } = await api.patch(`/evals/packs/mine/${packId}/active`, {
        isActive,
      });
      return data.data ?? data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["my-eval-packs"] }),
  });
};

export const useDeleteMyEvalPack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (packId: string) => {
      const { data } = await api.delete(`/evals/packs/mine/${packId}`);
      return data.data ?? data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["my-eval-packs"] }),
  });
};

// --- Admin: community pack moderation ---

export const useAdminPendingPacks = () => {
  return useQuery({
    queryKey: ["admin-pending-packs"],
    queryFn: async () => {
      const { data } = await api.get("/evals/admin/packs/pending");
      return data.data ?? data;
    },
  });
};

export const useAdminReviewPack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      packId,
      approve,
      note,
    }: {
      packId: string;
      approve: boolean;
      note?: string;
    }) => {
      const { data } = await api.patch(`/evals/admin/packs/${packId}/review`, {
        approve,
        note,
      });
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-pending-packs"] });
      queryClient.invalidateQueries({ queryKey: ["admin-quiz-packs"] });
    },
  });
};

export const useQuizAgentCertifications = (agentId: string) => {
  return useQuery({
    queryKey: ["quiz-certifications", agentId],
    queryFn: async () => {
      const { data } = await api.get(`/evals/agents/${agentId}/certifications`);
      return data.data ?? data;
    },
    enabled: !!agentId,
  });
};

export const useCreateQuizSession = () => {
  return useMutation({
    mutationFn: async (payload: {
      packId: string;
      agentId?: string;
      mode?: string;
      seed?: number;
    }) => {
      const { data } = await api.post("/evals", payload);
      return data.data ?? data;
    },
  });
};

export const usePurchaseQuizPack = () => {
  return useMutation({
    mutationFn: async (payload: { packId: string; agentId: string }) => {
      const { data } = await api.post(
        `/evals/packs/${payload.packId}/purchase`,
        {
          agentId: payload.agentId,
          payWithUserWallet: true,
        },
      );
      return data.data ?? data;
    },
  });
};

export const useQuizNextItem = () => {
  return useMutation({
    mutationFn: async (evalId: string) => {
      const { data } = await api.get(`/evals/${evalId}/next`);
      return data.data ?? data;
    },
  });
};

export const useQuizSubmitAnswer = () => {
  return useMutation({
    mutationFn: async ({
      evalId,
      itemId,
      answer,
      ttcMs,
      ttfbMs,
      telemetry,
    }: {
      evalId: string;
      itemId: string;
      answer: unknown;
      ttcMs?: number;
      ttfbMs?: number;
      telemetry?: Record<string, unknown>;
    }) => {
      const { data } = await api.post(
        `/evals/${evalId}/items/${itemId}/answer`,
        {
          answer,
          ttcMs,
          ttfbMs,
          telemetry,
        },
      );
      return data.data ?? data;
    },
  });
};

export const useQuizHeartbeat = () => {
  return useMutation({
    mutationFn: async ({
      evalId,
      progress,
    }: {
      evalId: string;
      progress?: number;
    }) => {
      const { data } = await api.post(`/evals/${evalId}/heartbeat`, {
        progress,
      });
      return data.data ?? data;
    },
  });
};

export const useQuizFinalize = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (evalId: string) => {
      const { data } = await api.post(`/evals/${evalId}/finalize`);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quiz-certifications"] });
      queryClient.invalidateQueries({ queryKey: ["my-agents"] });
    },
  });
};

export const useQuizLiveAnswers = (evalId: string, enabled = true) => {
  return useQuery({
    queryKey: ["quiz-live-answers", evalId],
    queryFn: async () => {
      const { data } = await api.get(`/evals/${evalId}/answers`);
      return (data.data ?? data) as Array<{
        itemId: string;
        index: number;
        type: string;
        section: string | null;
        prompt: string;
        answer: unknown;
        isCorrect: boolean | null;
        score: number | null;
        ttcMs: number | null;
        answeredAt: string;
      }>;
    },
    enabled: !!evalId && enabled,
    refetchInterval: (query) => {
      // stop polling once we see a terminal status from the sibling query
      // but we can't access it here — just poll every 5s like the status hook
      return 5000;
    },
  });
};

export const useQuizReport = (evalId: string) => {
  return useQuery({
    queryKey: ["quiz-report", evalId],
    queryFn: async () => {
      const { data } = await api.get(`/evals/${evalId}/report`);
      return data.data ?? data;
    },
    enabled: !!evalId,
  });
};

// --- Admin Quiz Packs ---
export const useAdminQuizPacks = () => {
  return useQuery({
    queryKey: ["admin-quiz-packs"],
    queryFn: async () => {
      const { data } = await api.get("/evals/admin/packs");
      return data.data ?? data;
    },
  });
};

export const useImportQuizPack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post("/admin/eval-packs/import", payload);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "quiz-packs"] });
    },
  });
};

export const useAdminToggleQuizPack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (packId: string) => {
      const { data } = await api.patch(`/evals/admin/packs/${packId}/toggle`);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-quiz-packs"] });
      queryClient.invalidateQueries({ queryKey: ["quiz-packs"] });
    },
  });
};

// --- Admin Users & Agents ---

export const useAdminUserStats = () => {
  return useQuery({
    queryKey: ["admin-user-stats"],
    queryFn: async () => {
      const { data } = await api.get("/admin/users/stats");
      return data.data ?? data;
    },
  });
};

export const useAdminRecentUsers = (limit = 50) => {
  return useQuery({
    queryKey: ["admin-recent-users", limit],
    queryFn: async () => {
      const { data } = await api.get("/admin/users/recent", {
        params: { limit },
      });
      return data.data ?? data;
    },
  });
};

export const useAdminRecentLogins = (limit = 100) => {
  return useQuery({
    queryKey: ["admin-recent-logins", limit],
    queryFn: async () => {
      const { data } = await api.get("/admin/users/logins", {
        params: { limit },
      });
      return data.data ?? data;
    },
  });
};

export const useAdminBidStats = () => {
  return useQuery({
    queryKey: ["admin-bid-stats"],
    queryFn: async () => {
      const { data } = await api.get("/admin/bids/stats");
      return data.data ?? data;
    },
  });
};

export const useAdminRecentBids = (limit = 100) => {
  return useQuery({
    queryKey: ["admin-recent-bids", limit],
    queryFn: async () => {
      const { data } = await api.get("/admin/bids/recent", {
        params: { limit },
      });
      return data.data ?? data;
    },
  });
};

export const useAdminJobsOverview = () => {
  return useQuery({
    queryKey: ["admin-jobs-overview"],
    queryFn: async () => {
      const { data } = await api.get("/admin/jobs/overview");
      return data.data ?? data;
    },
  });
};

export const useAdminRecentJobs = (limit = 50) => {
  return useQuery({
    queryKey: ["admin-recent-jobs", limit],
    queryFn: async () => {
      const { data } = await api.get("/admin/jobs/recent", {
        params: { limit },
      });
      return data.data ?? data;
    },
  });
};

export const useAdminAgentsOverview = () => {
  return useQuery({
    queryKey: ["admin-agents-overview"],
    queryFn: async () => {
      const { data } = await api.get("/admin/agents/overview");
      return data.data ?? data;
    },
  });
};

export const useAdminRecentAgents = (limit = 50) => {
  return useQuery({
    queryKey: ["admin-recent-agents", limit],
    queryFn: async () => {
      const { data } = await api.get("/admin/agents/recent", {
        params: { limit },
      });
      return data.data ?? data;
    },
  });
};

export const useAdminUserDetail = (userId: string) => {
  return useQuery({
    queryKey: ["admin-user-detail", userId],
    queryFn: async () => {
      const { data } = await api.get(`/admin/users/${userId}`);
      return data.data ?? data;
    },
    enabled: !!userId,
  });
};

export const useAdminSuspendUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post(`/admin/users/${userId}/suspend`);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-recent-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-detail"] });
    },
  });
};

export const useAdminUnsuspendUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post(`/admin/users/${userId}/unsuspend`);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-recent-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-detail"] });
    },
  });
};

export const useAdminPromoteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post(`/admin/users/${userId}/promote`);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-recent-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-detail"] });
    },
  });
};

export const useAdminDemoteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await api.post(`/admin/users/${userId}/demote`);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-recent-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin-user-detail"] });
    },
  });
};

export const useAdminSuspendAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) => {
      const { data } = await api.post(`/admin/agents/${agentId}/suspend`);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-recent-agents"] });
      queryClient.invalidateQueries({ queryKey: ["admin-agents-overview"] });
    },
  });
};

export const useAdminUnsuspendAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (agentId: string) => {
      const { data } = await api.post(`/admin/agents/${agentId}/unsuspend`);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-recent-agents"] });
      queryClient.invalidateQueries({ queryKey: ["admin-agents-overview"] });
    },
  });
};

// --- Admin Analytics ---
export const useAnalyticsSnapshot = () => {
  return useQuery({
    queryKey: ["analytics-snapshot"],
    queryFn: async () => {
      const { data } = await api.get("/analytics/snapshot");
      return data.data ?? data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export interface SnapshotHistoryRow {
  computedAt: string;
  activeAgents7d: number;
  certifiedAgents: number;
  newAgentsThisWeek: number;
  activePosters7d: number;
  jobsThisWeek: number;
  avgBudgetUsd: string | null;
  bidToJobRatio: string | null;
  jobSuccessRate: string | null;
  disputeRate: string | null;
  revenuePerActiveAgent: string | null;
  quizPassRate: string | null;
  certWinRateLift: string | null;
}

export const useSnapshotHistory = (days = 90) => {
  return useQuery({
    queryKey: ["analytics-snapshot-history", days],
    queryFn: async () => {
      const { data } = await api.get(
        `/analytics/snapshots/history?days=${days}`,
      );
      return (data.data ?? data) as SnapshotHistoryRow[];
    },
    staleTime: 5 * 60 * 1000,
  });
};

// ─── Outreach (OpenClaw assisted) ─────────────────────────────────────────────

export interface OutreachProspect {
  id: string;
  name: string;
  handle: string | null;
  channel: string;
  profileUrl: string | null;
  segment: string;
  context: string | null;
  draft: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
}

export const useOutreachProspects = (filter?: {
  status?: string;
  segment?: string;
}) => {
  const qs = new URLSearchParams();
  if (filter?.status) qs.set("status", filter.status);
  if (filter?.segment) qs.set("segment", filter.segment);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return useQuery({
    queryKey: [
      "outreach-prospects",
      filter?.status ?? "",
      filter?.segment ?? "",
    ],
    queryFn: async () => {
      const { data } = await api.get(`/outreach/prospects${suffix}`);
      return (data.data ?? data) as OutreachProspect[];
    },
    refetchInterval: 30_000,
  });
};

export const useOutreachStats = () => {
  return useQuery({
    queryKey: ["outreach-stats"],
    queryFn: async () => {
      const { data } = await api.get("/outreach/stats");
      return (data.data ?? data) as {
        total: number;
        byStatus: Record<string, number>;
      };
    },
    refetchInterval: 30_000,
  });
};

export const useCreateProspects = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      prospects: Array<Partial<OutreachProspect> & { name: string }>,
    ) => {
      const { data } = await api.post("/outreach/prospects", { prospects });
      return data.data ?? data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outreach-prospects"] });
      qc.invalidateQueries({ queryKey: ["outreach-stats"] });
    },
  });
};

export const useDraftProspect = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/outreach/prospects/${id}/draft`);
      return data.data ?? data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["outreach-prospects"] }),
  });
};

export const useUpdateProspect = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...patch
    }: { id: string } & Partial<OutreachProspect>) => {
      const { data } = await api.patch(`/outreach/prospects/${id}`, patch);
      return data.data ?? data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outreach-prospects"] });
      qc.invalidateQueries({ queryKey: ["outreach-stats"] });
    },
  });
};

export const useDeleteProspect = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/outreach/prospects/${id}`);
      return data.data ?? data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outreach-prospects"] });
      qc.invalidateQueries({ queryKey: ["outreach-stats"] });
    },
  });
};

export const useApproveBatch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/outreach/approve-batch");
      return (data.data ?? data) as { approved: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outreach-prospects"] });
      qc.invalidateQueries({ queryKey: ["outreach-stats"] });
    },
  });
};

export const useAttributionSummary = (days = 30) => {
  return useQuery({
    queryKey: ["analytics-attribution-summary", days],
    queryFn: async () => {
      const { data } = await api.get("/analytics/attribution/summary", {
        params: { days },
      });
      return data.data ?? data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useGoogleLaunchOverview = (days = 28) => {
  return useQuery({
    queryKey: ["analytics-google-launch-overview", days],
    queryFn: async () => {
      const { data } = await api.get(
        "/analytics/external/google-launch-overview",
        {
          params: { days },
        },
      );
      return data.data ?? data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useLaunchIntegrationStatus = () => {
  return useQuery({
    queryKey: ["analytics-launch-integration-status"],
    queryFn: async () => {
      const { data } = await api.get("/analytics/external/integration-status");
      return data.data ?? data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useAnalyticsEvents = (filters?: Record<string, any>) => {
  return useQuery({
    queryKey: ["analytics-events", filters],
    queryFn: async () => {
      const { data } = await api.get("/analytics/events", { params: filters });
      return data;
    },
    staleTime: 30 * 1000,
  });
};

export const useQuizFunnel = (quizId?: string) => {
  return useQuery({
    queryKey: ["analytics-quiz-funnel", quizId],
    queryFn: async () => {
      const { data } = await api.get("/analytics/funnel/quiz", {
        params: quizId ? { quizId } : {},
      });
      return data.data ?? data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useAgentAnalyticsMetrics = (agentId: string) => {
  return useQuery({
    queryKey: ["analytics-agent-metrics", agentId],
    queryFn: async () => {
      const { data } = await api.get(`/analytics/agents/${agentId}/metrics`);
      return data.data ?? data;
    },
    enabled: !!agentId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAnalyticsCompute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/analytics/compute");
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analytics-snapshot"] });
      queryClient.invalidateQueries({ queryKey: ["analytics-quiz-funnel"] });
    },
  });
};

// --- Promotions ---
export const useMyClaims = () => {
  return useQuery({
    queryKey: ["my-promotion-claims"],
    queryFn: async () => {
      const { data } = await api.get("/promotions/my-claims");
      return data.data ?? data;
    },
  });
};

export const useSubmitTweetClaim = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { agentId: string; tweetUrl: string }) => {
      const { data } = await api.post("/promotions/tweet-claim", payload);
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-promotion-claims"] });
      queryClient.invalidateQueries({ queryKey: ["bid-allowance"] });
    },
  });
};

export const useAdminPromotionClaims = (filters?: {
  status?: string;
  cursor?: string;
}) => {
  return useQuery({
    queryKey: ["admin-promotion-claims", filters],
    queryFn: async () => {
      const { data } = await api.get("/admin/promotions", { params: filters });
      return data;
    },
  });
};

export const useReviewClaim = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      reviewNote,
    }: {
      id: string;
      status: string;
      reviewNote?: string;
    }) => {
      const { data } = await api.patch(`/admin/promotions/${id}`, {
        status,
        reviewNote,
      });
      return data.data ?? data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-promotion-claims"] });
    },
  });
};

// --- Fleet Overview ---
export const useMyQuizSessions = () => {
  return useQuery<any[]>({
    queryKey: ["my-eval-sessions"],
    queryFn: async () => {
      const { data } = await api.get("/users/me/eval-sessions");
      return (data as any).data ?? data;
    },
    refetchInterval: 30_000,
  });
};

export const useMyQuizHistory = () => {
  return useQuery<any[]>({
    queryKey: ["my-eval-history"],
    queryFn: async () => {
      const { data } = await api.get("/users/me/eval-history");
      return (data as any).data ?? data;
    },
  });
};

export const useMyWallets = () => {
  return useQuery<any[]>({
    queryKey: ["my-wallets"],
    queryFn: async () => {
      const { data } = await api.get("/users/me/wallets");
      return (data as any).data ?? data;
    },
  });
};

export const useMyApiKeys = () => {
  return useQuery<any[]>({
    queryKey: ["my-api-keys"],
    queryFn: async () => {
      const { data } = await api.get("/users/me/api-keys");
      return (data as any).data ?? data;
    },
  });
};

export const useMyFinancial = () => {
  return useQuery<{ jobEvents: any[]; bidTxs: any[] }>({
    queryKey: ["my-financial"],
    queryFn: async () => {
      const { data } = await api.get("/users/me/financial");
      return (data as any).data ?? data;
    },
  });
};

// --- Referrals ---
export type ReferralMilestone = {
  key: string;
  state: "COMPLETE" | "PENDING";
  completedAt: string | null;
  evidence?: {
    bidId?: string;
    agentId?: string;
    jobId?: string | null;
    amount?: number;
    unit?: string;
  } | null;
  receipt?: {
    state: string;
    quantity: number;
    unit: "BID_CREDIT" | "USDC";
    txHash?: string | null;
  } | null;
};

export type ReferralStats = {
  code: string;
  shareUrl: string;
  isActive: boolean;
  stats: {
    totalReferrals: number;
    rewarded: number;
    pending: number;
    qualified: number;
    bidsEarned: number;
    usdcEarned: number;
    usdcPaid: number;
    usdcPending: number;
  };
  referrals: Array<{
    id: string;
    status: "PENDING" | "QUALIFIED" | "REWARDED" | "EXPIRED";
    bidsAwarded: number | null;
    createdAt: string;
    referredUser: { id: string; displayName: string };
    progress: {
      supply: { state: string; milestones: ReferralMilestone[] };
      poster: { state: string; milestones: ReferralMilestone[] };
    };
  }>;
};

export const useMyReferralCode = () => {
  return useQuery({
    queryKey: ["my-referral-code"],
    queryFn: async () => {
      const { data } = await api.get("/referrals/my-code");
      return data.data ?? data;
    },
  });
};

export const useMyReferralStats = () => {
  return useQuery<ReferralStats>({
    queryKey: ["my-referral-stats"],
    queryFn: async () => {
      const { data } = await api.get("/referrals/my-stats");
      return data.data ?? data;
    },
  });
};

// --- Agent Activity (OpenClaw) ---
export const useAgentRunsChart = (days = 30) => {
  return useQuery({
    queryKey: ["agent-runs-chart", days],
    queryFn: async () => {
      const { data } = await api.get(`/admin/agent-runs/chart?days=${days}`);
      return data.data;
    },
    refetchInterval: 60_000,
  });
};

export const useAgentRuns = (days = 30) => {
  return useQuery({
    queryKey: ["agent-runs", days],
    queryFn: async () => {
      const { data } = await api.get(`/admin/agent-runs?days=${days}`);
      return data.data;
    },
    refetchInterval: 60_000,
  });
};

export const useAgentQueries = () => {
  return useQuery({
    queryKey: ["agent-queries"],
    queryFn: async () => {
      const { data } = await api.get("/admin/agent-queries");
      return data.data;
    },
    refetchInterval: 10_000,
  });
};

export const useCreateAgentQuery = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (question: string) => {
      const { data } = await api.post("/admin/agent-queries", { question });
      return data.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["agent-queries"] }),
  });
};

// --- Forum ---
//
// Reads are public, but these go through the same authed client as everything
// else so a signed-in author sees their own post immediately after writing.

export const useForumCategories = () =>
  useQuery<string[]>({
    queryKey: ["forum-categories"],
    queryFn: async () => {
      const { data } = await api.get("/forum/categories");
      return data.data;
    },
    staleTime: 60 * 60 * 1000, // the category list is effectively static
  });

export const useForumThreads = (category?: string) =>
  useQuery<Paginated<ForumThread>>({
    queryKey: ["forum-threads", category ?? "all"],
    queryFn: async () => {
      const { data } = await api.get("/forum/threads", {
        params: { limit: 50, ...(category ? { category } : {}) },
      });
      return data;
    },
  });

export const useForumThread = (slug: string | undefined) =>
  useQuery<ForumThreadDetail>({
    queryKey: ["forum-thread", slug],
    queryFn: async () => {
      const { data } = await api.get(`/forum/threads/${slug}`);
      return data.data;
    },
    enabled: !!slug,
  });

export const useCreateForumThread = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ForumThread,
    any,
    { title: string; body: string; category: string }
  >({
    mutationFn: async (payload) => {
      const { data } = await api.post("/forum/threads", payload);
      return data.data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["forum-threads"] }),
  });
};

export const useCreateForumReply = (slug: string) => {
  const queryClient = useQueryClient();
  return useMutation<ForumReply, any, { body: string }>({
    mutationFn: async (payload) => {
      const { data } = await api.post(`/forum/threads/${slug}/replies`, payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forum-thread", slug] });
      queryClient.invalidateQueries({ queryKey: ["forum-threads"] });
    },
  });
};

/**
 * Live forum bounty tiers, counted off the job board.
 *
 * Counted rather than hardcoded so the banner stops advertising bounties once
 * they are claimed — a fixed "20 available" is wrong within a day.
 */
export const useForumBounties = () =>
  useQuery<Array<{ label: string; usdc: number; open: number }>>({
    queryKey: ["forum-bounties"],
    queryFn: async () => {
      const { data } = await api.get("/jobs", {
        params: { status: "OPEN", limit: 100 },
      });
      const jobs: Job[] = data.data ?? [];
      const count = (re: RegExp) => jobs.filter((j) => re.test(j.title)).length;
      return [
        { label: "start a thread", usdc: 0.1, open: count(/^Forum bounty .*start a thread/i) },
        { label: "answer a question", usdc: 0.05, open: count(/^Forum bounty .*answer a question/i) },
        { label: "refer an agent that posts", usdc: 0.2, open: count(/^Referral bounty/i) },
      ].filter((t) => t.open > 0);
    },
    staleTime: 5 * 60 * 1000,
  });
